import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppColors, config, typographyClamps } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
import GetStartedModel from '../components/models/GetStartedModel';
import VideoModel from '../components/models/VideoModel';
import { HtmlRenderer } from '../components/HtmlRenderer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { HomeSectionView } from '../components/HomeSectionView';
import { OrangeRoundedButton } from '../components/OrangeRoundedButton';
import BookDemoFormModel from '../components/models/BookDemoFormModel';
import ImagePreviewDialog from '../components/models/ImagePreviewDialog';
import AudioModel from '../components/models/AudioModel';

interface GradientBorderBoxProps {
  children: React.ReactNode;
  gradient: string;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number;
}

const GradientBorderBox: React.FC<GradientBorderBoxProps> = ({
  children,
  gradient,
  borderWidth = 3,
  borderRadius = 24,
  padding = 1,
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        padding: `${borderWidth}px`,
        background: gradient,
        borderRadius: `${borderRadius}px`,
        marginLeft: 'auto',
        width: 'fit-content',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: `${borderRadius - borderWidth}px`,
          padding: padding,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface Feature {
  iconPath: string;
  title?: string;
  description?: string;
  subtitle1?: string;
  subtitle2?: string;
  imageOnLeft?: boolean;
  videoUrl?: string;
  audioUrl?: string;
  link?: string;
}

// const mainFeatureCars: Feature[] = [
//   {
//     title: 'Scan the Lot',
//     description: 'Every Scan becomes a captured, routed buyer',
//     iconPath: '/static/home_page/img_scan_the_lot.png',
//     imageOnLeft: false,
//     link: '/the-lot',
//   },
//   {
//     title: 'Enter the web',
//     description: 'Every online visitor is guided into a controlled lead path',
//     iconPath: '/static/home_page/img_enter_the_web.png',
//     imageOnLeft: false,
//     link: '/the-web',
//   },
//   {
//     title: 'Enter my phone',
//     description:
//       'Every call is captured, traced and routed to the right sales person',
//     iconPath: '/static/home_page/img_enter_my_phone.png',
//     imageOnLeft: true,
//     link: '/#',
//   },
// ];

const audioCards: Feature[] = [
  {
    iconPath: '/static/home_page/img_audio_1.png',
    audioUrl: '/audio/What_Creates_a_Quality_Lead.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_2.png',
    audioUrl: '/audio/Why_Buyer_Behavior_Matters.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_3.png',
    audioUrl: '/audio/What_is_Ghost_Data.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_4.png',
    audioUrl: '/audio/Spotlight_vs_VDP.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_5.png',
    audioUrl: '/audio/What_Is_the_QRBuddy_Web_Raill.wav',
  },
  {
    iconPath: '/static/home_page/img_audio_6.png',
    audioUrl: '/audio/Creating_Quality_Leads.wav',
  },
];

// const featureVideoCards: Feature[] = [
//   {
//     title: 'The First 60 Seconds That Decide the Sale',
//     description:
//       'The moment a shopper scans, clicks, or calls — the clock starts. This video reveals why real-time engagement in the first 60 seconds now determines who wins the sale, and how QR Buddy captures and routes buyers instantly across the lot, web, and phone.',
//     iconPath: '/static/home_page/video_thumb_1.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/First-60-Seconds.mp4',
//   },
//   {
//     title: 'The Problem with Modern Lead Systems',
//     description:
//       'Traditional lead systems are broken — selling names instead of building real buying experiences. In this video, Rob explains why speed, engagement, and first-moment connection now decide who wins the sale, and how QR Buddy rebuilds the front line of car sales for today’s buyer.',
//     iconPath: '/static/home_page/video_thumb_2.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/Problem-with-Lead-Systems.mp4',
//   },
//   {
//     title: 'High-Tech Meets High-Touch',
//     description:
//       'AI should empower people — not replace them. In this video, Rob explains how QR Buddy blends real-time AI engagement with human connection to protect your sales team, your reputation, and your long-term growth.',
//     iconPath: '/static/home_page/video_thumb_3.png',
//     videoUrl: 'https://assets.qrbuddy.ai/web/High-Tech-High-Touch.mp4',
//   },
// ];

const featureCards: Feature[] = [
  { iconPath: '/static/benefits_page/img_benefits_tile_5.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_6.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_1.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_2.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_7.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_8.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_3.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_4.png' },
  // { iconPath: '/static/benefits_page/img_benefits_tile_9.png' },
  // { iconPath: '/static/benefits_page/img_benefits_tile_10.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_11.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_12.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_13.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_14.png' },
];

export interface ScreenZone {
  image: string;
  label: string;
  minX: number; maxX: number;
  minY: number; maxY: number;
}

// ── 6-screen horizontal strip (mobile screens) ──
const ZONES_MOBILE_SCREENS: ScreenZone[] = [
  { label: 'Vehicle Listing',    image: '/static/home_page/screen1_vehicle_listing.png', minX: 0.00, maxX: 0.17, minY: 0, maxY: 1 },
  { label: 'Share The Vehicle',  image: '/static/home_page/screen2_share_vehicle.png',   minX: 0.17, maxX: 0.33, minY: 0, maxY: 1 },
  { label: 'Test Drive',         image: '/static/home_page/screen3_test_drive.png',      minX: 0.33, maxX: 0.50, minY: 0, maxY: 1 },
  { label: 'Trade In',           image: '/static/home_page/screen4_trade_in.png',        minX: 0.50, maxX: 0.66, minY: 0, maxY: 1 },
  { label: 'Virtual Walkaround', image: '/static/home_page/screen5_walkaround.png',      minX: 0.66, maxX: 0.82, minY: 0, maxY: 1 },
  { label: 'Finance App',        image: '/static/home_page/screen6_finance.png',         minX: 0.82, maxX: 1.00, minY: 0, maxY: 1 },
];

// ── 4-screen 2×2 grid (command center) ──
const ZONES_COMMAND_CENTER: ScreenZone[] = [
  { label: 'Active Dashboard',  image: '/static/home_page/img_command_center_1.png', minX: 0.00, maxX: 0.50, minY: 0.00, maxY: 0.50 },
  { label: 'Active Inventory',  image: '/static/home_page/img_command_center_2.png', minX: 0.50, maxX: 1.00, minY: 0.00, maxY: 0.50 },
  { label: 'View Ghost Leads',  image: '/static/home_page/img_command_center_3.png', minX: 0.00, maxX: 0.50, minY: 0.50, maxY: 1.00 },
  { label: 'Track Sales Reps',  image: '/static/home_page/img_command_center_4.png', minX: 0.50, maxX: 1.00, minY: 0.50, maxY: 1.00 },
];

// ── 2-screen horizontal split (dealership in pocket) ──
const ZONES_DEALERSHIP_IN_POCKET: ScreenZone[] = [
  { label: 'Dealership In Pocket 1', image: '/static/home_page/img_dealership_in pocket_1.png', minX: 0.00, maxX: 0.40, minY: 0, maxY: 1 },
  { label: 'Dealership In Pocket 2', image: '/static/home_page/img_dealership_in pocket_2.png', minX: 0.40, maxX: 1.00, minY: 0, maxY: 1 },
];

// Zones ordered front → back so overlapping areas resolve to the frontmost screen
const ZONES_STACKED_SCREENS: ScreenZone[] = [
  // ── Right side: SMS mobile phone (no overlap, checked first for right half) ──
  {
    label: 'SMS Notification',
    image: '/static/home_page/img_notification_mobile.png',
    minX: 0.65, maxX: 1.00,
    minY: 0.00, maxY: 1.00,
  },
  // ── Front-most desktop screen (Real-Time Dash, bottom) ──
  {
    label: 'Real-Time Dash',
    image: '/static/home_page/img_sales_rep_dash_3.png',
    minX: 0.14, maxX: 0.65,
    minY: 0.42, maxY: 1.00,
  },
  // ── Middle desktop screen (Track Sales Leads) ──
  {
    label: 'Track Sales Leads',
    image: '/static/home_page/img_sales_rep_dash_2.png',
    minX: 0.08, maxX: 0.65,
    minY: 0.20, maxY: 0.58,
  },
  // ── Back-most desktop screen (Messaging, top) ──
  {
    label: 'Messaging',
    image: '/static/home_page/img_sales_rep_dash_1.png',
    minX: 0.00, maxX: 0.55,
    minY: 0.00, maxY: 0.38,
  },
];



interface FAQSection {
  title: string;
  questions: FAQItem[];
}
interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQSection[] = [
  {
    title: '1️⃣ FUNCTIONALITY & FEATURES',
    questions: [
      {
        question: 'How does QR Buddy actually identify the shopper?',
        answer:
          'When a customer scans the QR Buddy code, a one-tap login screen (Facebook, Apple, or Google) verifies their identity instantly. That means the dealer gets their name, phone number, and vehicle interest immediately — no forms and no friction.',
      },

      {
        question: 'Does the customer need to download an app?',
        answer:
          'No. QR Buddy is 100% mobile-web. The customer just scans and engages — no downloads, no accounts, no hassle.',
      },
      {
        question: 'What does the customer see when they scan?',
        answer:
          'They see the exact vehicle they are standing in front of: full photos, pricing, options, comparisons, and an AI assistant that can answer questions in real time.',
      },
      {
        question: 'What happens if the shopper views multiple vehicles?',
        answer:
          'All scan activity is tied to the same verified customer. The dealer sees exactly which vehicles they scanned, favorited, and compared — giving immediate insight into buyer intent.',
      },
      {
        question: 'How does the AI help customers?',
        answer:
          'Our AI answers questions instantly (features, mileage, availability), helps schedule test drives, and can push buyers into starting a finance app — even when reps aren’t available.',
      },
    ],
  },
  {
    title: '2️⃣ LEADS, PASS-OFF & COMMUNICATION',
    questions: [
      {
        question: 'How do salespeople get leads?',
        answer:
          'The moment a customer scans and logs in, the assigned rep receives a real-time alert with the shopper’s name, phone number, and the exact vehicle they’re interacting with.',
      },
      {
        question: 'What if no rep is available?',
        answer:
          'QR Buddy’s AI keeps the conversation alive: answering questions, confirming interest, and booking next-day appointments — so the lead never goes cold.',
      },
      {
        question: 'Are these internet leads or walk-in leads?',
        answer:
          'These are high-intent walk-ups and real shoppers — customers physically on the lot or browsing inventory online. It’s the highest-quality lead source a dealership can have.',
      },
      // {
      //   question: 'Can these leads be pushed into our CRM?',
      //   answer:
      //     'Yes. Leads, activity, and engagement data can be exported at any time and imported directly into your CRM.',
      // },
    ],
  },
  {
    title: '3️⃣ PRICING & BILLING',
    questions: [
      {
        question: 'What does the Pay-As-You-Go plan include?',
        answer:
          'Pay-As-You-Go captures after-hours leads from 6 PM to 10 AM for $40 per verified customer. No monthly fees, no long contracts — perfect for starting small.',
      },
      {
        question: 'What does the Subscription Plan include?',
        answer:
          'Our 90-day subscription (largest plan we offer):\n• $1,500 monthly platform access\n• $10 per customer lead (not per scan)\n• Full AI coverage, 24/7\n• Full analytics & reporting\n• Real-time dealer dashboard',
      },
      {
        question: 'Why only a 90-day contract?',
        answer:
          'Because QR Buddy is built on performance instead of long-term commitments. If it doesn’t produce, you’re not stuck.',
      },
      {
        question: 'Are setup fees required?',
        answer:
          'Yes. $1,495 one-time setup covers your dashboard, QR system activation, and onboarding.',
      },
      {
        question:
          'What happens if a dealership’s wallet balance goes under $200 and the automatic payment fails? Do they get a warning or grace period before they’re locked out?',
        answer:
          'When the dealership’s wallet drops to $200, they receive multiple alerts: one at $200, another at $150, another at $50, and a final alert when the balance hits zero. They also see notifications inside the platform so they have plenty of warning before any lockout occurs.',
      },
      {
        question:
          'For the subscription model, if we stop after the 90-day contract, is there any penalty or cancellation fee?',
        answer:
          'QR Buddy is pay-as-you-go. If a dealership decides not to continue after 90 days, they simply stop payments and the system automatically shuts down after 14 days of non-payment. There’s no penalty or cancellation fee. If they return later, a $299 reactivation fee applies and access resumes immediately.',
      },
      {
        question:
          'If a dealership on the subscription model runs out of its lead balance mid-month, can it top up right away, or must it wait for the next billing cycle?',
        answer:
          'Yes — they can top up anytime. Even with a paid subscription, the lead wallet works the same way as the pay-as-you-go model. When their balance gets low, alerts appear at $200, $150, and $50. They can add funds instantly to keep leads flowing at the $10 rate. If they don’t, leads will temporarily route to corporate until the wallet is replenished.',
      },
      {
        question:
          'Can a dealership switch from the wallet-based model to the subscription model later on?',
        answer:
          'Absolutely. Switching is simple and the $1,495 setup fee does not repeat. They just prepay the 90-day subscription period ($1,500 per month × 3 = $4,500) and their account converts to the flat $10 per-lead rate instead of the time-based pricing.',
      },
      {
        question:
          'Are there reporting or analytics tools to track leads and spending?',
        answer:
          'Yes. The dealership dashboard tracks total leads, leads acted on, and average disposition time (how quickly reps respond). Managers can filter by day or week to see performance trends. Each rep also has a personal dashboard showing the same metrics. The AI selector uses these stats to route new leads to reps who respond fastest, maximizing ROI.',
      },
      {
        question: 'If we have a slow month, do unused lead credits roll over?',
        answer:
          'Yes. Lead credits in the wallet never expire. Unused credits simply remain until used. This makes the system flexible for both high- and low-volume dealerships without losing value month to month.',
      },
      {
        question:
          'How does reactivation work if an account is locked for non-payment?',
        answer:
          'Dealerships see a lockout notice on login with instructions to call QR Buddy. They pay the $299 reactivation fee, reload their wallet, and their access restores immediately. A future update will let them self-reactivate directly inside the platform for even faster reinstatement.',
      },
      {
        question:
          'Are there any extra or hidden fees beyond setup, leads, and possible reactivation?',
        answer:
          'No hidden or recurring surprise costs. The only optional expense is for marketing materials—table tents, rack cards, signs, etc.—available inside the platform to help promote QR Buddy on-site. Everything else is fully included.',
      },
      {
        question:
          'If a dealership has multiple locations, can all be managed under one account?',
        answer:
          'Each location (rooftop) has its own account and subscription because each operates independently with unique inventory and lead flow. However, multi-store owners can access a centralized corporate view that lets them switch between locations for oversight and reporting.',
      },
    ],
  },
  {
    title: '4️⃣ SETUP, SIGNAGE & INVENTORY',
    questions: [
      {
        question: 'How do we get our vehicles into QR Buddy?',
        answer:
          'Simply upload your inventory CSV. QR Buddy automatically generates your QR codes and 5×7 window signage for every vehicle — instantly.',
      },
      {
        question: 'Do you provide promotional signage for the lot?',
        answer:
          'Yes. Dealers can order window stickers, table tents, and lot signs directly from the QR Buddy store — usually shipped within 2 business days.',
      },
      {
        question: 'How long does full setup take?',
        answer:
          'About 24 hours for activation + uploading inventory + signage placement. Dealers can literally be live the next day.',
      },
      {
        question: 'How does QR Buddy handle training and onboarding?',
        answer:
          'Every dealership is assigned a dedicated sales rep for personalized onboarding. The platform also includes Miles, a 24/7 AI assistant for instant setup help, plus an in-app library of how-to videos. Corporate email support replies within one business day. Combined, these tools make getting started smooth and fully supported.',
      },
    ],
  },
];

const Home: React.FC = () => {
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  // const xMargin = { xs: 0, sm: 0, md: '8%', lg: '10%' };

  // const headerActionButtonImageSize = {
  //   xs: '100% auto',
  //   sm: '100% auto',
  //   md: '100% auto',
  //   lg: '100% 100%',
  // };

  const bottomPadding = {
    xs: '-12%',
    sm: '-12%',
    md: '-12%',
    lg: '-10%',
    xl: '-7%',
  };

  const navigate = useNavigate();

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [audioUrl, setAudioUrl] = useState<string | undefined>();
  // const [isPlayIntroVideo] = useState<boolean>(false);

  const [expanded, setExpanded] = useState<string | false>(false);

  const scrollRefHowItWorks = useRef<HTMLDivElement>(null);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const { toggle, onOpen, onClose } = useToggle();
  const { toggle: audioToggle, onOpen: audioOnOpen, onClose: audioOnClose } = useToggle();

  const {
    toggle: toggleRequestDemoModel,
    onOpen: onOpenRequestDemoModel,
    onClose: onCloseRequestDemoModel,
  } = useToggle();

  const {
    toggle: toggleGetStartedModel,
    // onOpen: onOpenGetStartedModel,
    onClose: onCloseGetStartedModel,
  } = useToggle();

  const [previewImagePath, setPreviewImagePath] = useState<
    string | undefined
  >();

  const {
    toggle: toggleImagePreviewModel,
    onOpen: onOpenImagePreviewModel,
    onClose: onCloseImagePreviewModel,
  } = useToggle();

  const openImageViewView = (imagePath: string | undefined) => {
    if (imagePath !== undefined) {
      setPreviewImagePath(imagePath);
      onOpenImagePreviewModel();
    }
  };

  const buildClickHandler = (zones: ScreenZone[]) =>
  (e: React.MouseEvent<HTMLImageElement>) => {
    const img  = e.currentTarget as HTMLImageElement;
    const rect = img.getBoundingClientRect();
    const rx   = (e.clientX - rect.left) / rect.width;
    const ry   = (e.clientY - rect.top)  / rect.height;

    const matched = zones.find(
      (z) => rx >= z.minX && rx < z.maxX && ry >= z.minY && ry < z.maxY
    );
    if (matched) openImageViewView(matched.image);
  };

  useEffect(() => {
    if (toggle === false) {
      setVideoUrl(undefined);
    }
  }, [toggle]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpen();
    }
  };


  useEffect(() => {
    if (audioToggle === false) {
      setAudioUrl(undefined);
    }
  }, [audioToggle]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayAudio = (audioUrl: string | undefined) => {
    if (audioUrl !== undefined) {
      setAudioUrl(audioUrl);
      audioOnOpen();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCallToAssistant = () => {
    let telLink = `tel:${config.AUTO_ASSISTANT_CONTACT_NUMBER}`;
    window.open(`${telLink}`, '_blank') ||
      window.location.replace(`${telLink}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const scrollToHowItWorks = () => {
    const el = scrollRefHowItWorks.current;
    if (!el) return;

    // el?.scrollIntoView({ behavior: 'smooth' });

    const yOffset = -50; // scroll 50px less (adjust as needed)
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigateToHowItWorksPage = () => {
    navigate('/how-it-works');
  };

  const handleGetStartedClick = () => {
    navigate('/pricing');
  };

  const navigateToPage = (page: string) => {
    navigate(page);
  };

  const getBookADemoButton = () => {
    return (
      <Button
        variant='contained'
        sx={{
          ml: { xs: 0, sm: 2 },
          width: 'fit-content',
          height: 42,
          borderRadius: '12px',
          border: 0,
          color: 'white',
          padding: '10px 24px',
          backgroundColor: AppColors.vividBlueColor2,
          fontWeight: 'bold',
          textTransform: 'none',
          fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
          lineHeight: 1.5,
        }}
        onClick={onOpenRequestDemoModel}
      >
        Book a Demo
      </Button>
    );
  };

  const getLearnMoreAndGetStartedButtons = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          px: 3,
          position: 'relative',
        }}
      >
        <Button
          variant='contained'
          sx={{
            borderRadius: 0,
            bgcolor: AppColors.orangeBGColor,
            color: 'white',
            textTransform: 'none',
            padding: { xs: '4px 12px', sm: '8px 24px' },
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            // my: { xs: 2, sm: 4 },
            my: 1,
            '&:hover': {
              borderRadius: 0,
            },
          }}
          onClick={() => {
            // onOpenRequestDemoModel();
          }}
        >
          Learn More
        </Button>
        <Button
          variant='outlined'
          sx={{
            borderRadius: 0,
            borderColor: AppColors.vividBlueColor2,
            borderWidth: 2,
            color: AppColors.vividBlueColor2,
            backgroundColor: 'white',
            textTransform: 'none',
            padding: { xs: '4px 12px', sm: '8px 24px' },
            fontSize: typographyClamps.h6,
            whiteSpace: 'pre-line',
            lineHeight: 1.35,
            my: 1,
            '&:hover': {
              borderColor: AppColors.vividBlueColor2,
              backgroundColor: 'rgba(0, 0, 0, 0.04)', // optional subtle hover
              borderRadius: 0,
            },
          }}
          onClick={() => {
            handleGetStartedClick();
          }}
        >
          Get Started
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Page title={'Home'}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // backgroundImage: `url('/static/home_page/home_banner_background .png')`,
            // backgroundPosition: 'center',
            // backgroundSize: 'cover',
            // backgroundRepeat: 'no-repeat',
            background: AppColors.aiGradient8,
            p: { xs: 2, sm: 4 },
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(1.4rem, 5vw, 3.5rem)',
              fontWeight: 'bold',
              textAlign: 'center',
              whiteSpace: 'pre-line', // This allows text to break at newline characters
              lineHeight: 1.1,
            }}
          >
            Built Around the Buyer. Designed for the Dealer.
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: typographyClamps.subtitle1,
                sm: typographyClamps.h4,
              },
              textAlign: 'center',
              pt: 2,
              whiteSpace: 'pre-line', // This allows text to break at newline characters
            }}
          >
            Real experience. Real behavior. Real leads.
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component={'img'}
              src='/static/home_page/img_home_video.png'
              sx={{ maxWidth: '90%', p: 2, cursor: 'pointer' }}
              onClick={() => handlePlayVideo(config.HOME_PAGE_INTRO_VIDEO)}
            />
            <Box
              component={'img'}
              src='/static/images/ic_play_orange.png'
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                height: { xs: 50, sm: 80 },
                pointerEvents: 'none',
              }}
            />
          </Box>

          {/* <Stack
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-evenly',
              width: '90%',
              gap: 5,
            }}
          >
            {mainFeatureCars.map((item, idx) => (
              <Box sx={{ textAlign: 'center' }}>
                <Grid
                  key={`main-feature-${idx + 1}`}
                  container
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: '#0069e421',
                    borderRadius: 4,
                  }}
                >
                  {item.imageOnLeft && (
                    <Grid
                      size={5.5}
                      sx={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Box
                        component={'img'}
                        src={item.iconPath}
                        sx={{
                          height: {
                            xs: '200px',
                            sm: '150px',
                            md: '200px',
                          },
                          maxWidth: '100%',
                          // objectFit: 'fill',
                        }}
                      />
                    </Grid>
                  )}

                  <Grid size={6} sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize18,
                        fontWeight: 700,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize17,
                        // color: AppColors.grayColor,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Grid>

                  {!item.imageOnLeft && (
                    <Grid
                      size={6}
                      sx={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Box
                        component={'img'}
                        src={item.iconPath}
                        sx={{
                          height: {
                            xs: '200px',
                            sm: '150px',
                            md: '200px',
                          },
                          maxWidth: '100%',
                          objectFit: 'fill',
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                <Button
                  variant='contained'
                  sx={{
                    bgcolor: AppColors.orangeBGColor,
                    color: 'white',
                    borderRadius: 10,
                    px: 4,
                    py: 1,
                    my: 1,
                    fontSize: { xs: '0.9rem', sm: '1.2rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    alignSelf: 'center',
                  }}
                  onClick={() => item.link && navigateToPage(item.link)}
                >
                  Learn More
                </Button>
              </Box>
            ))}
          </Stack> */}
        </Box>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              background: `url('/static/home_page/header_actions_mobile_1.png')`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              aspectRatio: '652 / 366', // matches actual image dimensions
              maxHeight: 450,
            }}
          >
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-around',
                alignItems: 'flex-end',
                height: '100%',
                position: 'relative',
                bottom: bottomPadding,
              }}
            >
              <OrangeRoundedButton onClick={() => navigateToPage('/the-lot')}>
                Learn More
              </OrangeRoundedButton>
              <OrangeRoundedButton onClick={() => navigateToPage('/the-web')}>
                Learn More
              </OrangeRoundedButton>
            </Stack>
          </Grid>

          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              background: `url('/static/home_page/header_actions_mobile_2.png')`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              aspectRatio: '652 / 366',
              maxHeight: 450,
              mt: { xs: 5, sm: 0 },
            }}
          >
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-around',
                alignItems: 'flex-end',
                height: '100%',
                position: 'relative',
                bottom: bottomPadding,
              }}
            >
              <OrangeRoundedButton>Learn More</OrangeRoundedButton>
              <OrangeRoundedButton>Learn More</OrangeRoundedButton>
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            // width: '100%',
            textAlign: 'center',
            px: { xs: '5%', sm: '10%', md: '25%' },
            mx: 0,
            mt: { xs: 6, sm: 10 },
            mb: 4,
          }}
        >
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: '#0069e429',
              borderRadius: { xs: 4, sm: 6 },
            }}
          >
            <Box textAlign={'center'} mr={4}>
              <Box
                component={'img'}
                src='/static/images/rob_profile.png'
                sx={{
                  maxWidth: { xs: '120%', sm: '150%', md: '100%' },
                }}
              />
            </Box>
            <Stack
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.body1,
                    sm: typographyClamps.h5,
                  },
                  fontWeight: 400,
                }}
              >
                <Box
                  component={'img'}
                  src='/static/home_page/img_quotation_mark_end.png'
                  height={{ xs: '16px', sm: '24px' }}
                  sx={{
                    position: 'relative',
                    top: -10,
                    left: 0,
                    opacity: 0.45,
                  }}
                />
                &nbsp; “It starts with the experience. The experience creates
                the data. And the data creates the lead.”&nbsp;
                <Box
                  component={'img'}
                  src='/static/home_page/img_quotation_mark_start.png'
                  height={{ xs: '16px', sm: '24px' }}
                  sx={{ position: 'relative', bottom: 5, opacity: 0.45 }}
                />
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
            m: { xs: 2, sm: 4 },
            borderRadius: 4,
            bgcolor: '#0e1a3a',
            color: 'white',
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              // fontWeight: 'bold',
            }}
          >
            Short audio insights on buyer behavior, AutoSpotlight, and what
            truly creates a quality lead.
          </Typography>

          <Box
            sx={{
              position: 'relative',
              my: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: typographyClamps.h4,
              }}
            >
              Dealer Questions. Clear Answers.
            </Typography>

            <Box
              component={'img'}
              src='/static/home_page/img_orange_underline.png'
              sx={{
                display: { xs: 'none', sm: 'block' },
                width: { xs: '90px', sm: '200px' },
                position: 'absolute',
                left: 0,
                bottom: -10,
              }}
            />
          </Box>

          <Grid
            container
            gap={{ xs: 1, sm: 2 }}
            my={{ xs: 2, sm: 4 }}
            justifyContent={'center'}
          >
            {audioCards.map((item) => {
              return (
                <Grid
                  size={{ xs: 3.7, sm: 3.7, md: 1.8 }}
                  key={item.title}
                  display={'flex'}
                >
                  <Box
                    component={'img'}
                    src={item.iconPath}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      handlePlayAudio(item.audioUrl);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          {getBookADemoButton()}
        </Box>

        {/* <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            // alignItems: 'center',
            p: 2,
            gap: { xs: 2, sm: 4 },
          }}
        >
          {featureVideoCards.map((video, idx) => (
            <Grid
              key={`video-card-${idx + 1}`}
              size={{ xs: 12, sm: 3.5 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                // justifyContent: 'center',
              }}
            >
              <Box
                component={'img'}
                src={video.iconPath}
                sx={{ cursor: 'pointer' }}
                onClick={() => handlePlayVideo(video.videoUrl)}
              />

              <Box display={'flex'} alignItems={'center'} gap={1} mt={1}>
                <Typography sx={{ fontSize: typographyClamps.body1 }}>
                  Video
                </Typography>
                <YouTubeIcon fontSize='large' sx={{ color: 'red' }} />
              </Box>

              <Typography
                sx={{
                  fontSize: typographyClamps.fontSize17,
                  fontWeight: 'bold',
                }}
              >
                {video.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: typographyClamps.fontSize15,
                  fontWeight: 'bold',
                  mt: 1,
                }}
              >
                Brief Description:
              </Typography>
              <Typography sx={{ fontSize: typographyClamps.fontSize15 }}>
                {video.description}
              </Typography>
            </Grid>
          ))}
        </Grid> */}

        <Box>
          <HomeSectionView
            idx={0}
            label='<strong>Scanning</strong> on your lot'
            labelImagePath={'/static/home_page/ic_scanning.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Turn every vehicle into a live digital sales asset',
                description:
                  'A quick QR scan instantly identifies the vehicle and launches a guided mobile buying journey — capturing interest before the shopper ever walks away.',
              },
              {
                title: 'Replace static window stickers with live engagement',
                description:
                  'Shoppers see real pricing, availability, payments, and instant chat — right from the windshield.',
              },
              {
                title: 'Own every walk-up interaction',
                description:
                  'QR Buddy converts anonymous lot traffic into tracked, routed, qualified digital prospects — eliminating lost ups and skipped logins.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_scanning_on_your_lot.png'}
            openImageViewView={openImageViewView}
          />

          <HomeSectionView
            idx={1}
            label='Your <strong>Website</strong> Traffic '
            labelImagePath={'/static/home_page/ic_website_traffic.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Capture buyers before they bounce',
                description:
                  'The NEW QR Buddy Web Rail engages website visitors the moment they arrive — guiding their experience, capturing real-time behavior, and turning anonymous traffic into identified prospects before momentum fades.',
              },
              {
                title: 'Track real buying intent — not just page views',
                description:
                  'Just like we capture engagement on the lot, the Web Rail records buyer actions on your website — pricing clicks, trade-in exploration, finance interest, vehicle comparisons, and return visits — revealing who’s truly in-market.',
              },
              {
                title: 'Build a living digital buyer profile',
                description:
                  'Every interaction feeds into a dynamic, evolving buyer profile — combining lot scans and website behavior — so your team remains informed, follows up smarter, and closes with clarity.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_website_traffic.png'}
            openImageViewView={openImageViewView}
          />

          <HomeSectionView
            idx={2}
            label='Auto Spotlight'
            labelImagePath={'/static/home_page/ic_calling_dealership.png'}
            title=''
            subtitle=''
            features={[
              {
                title: 'Every Scan Becomes Measurable Intent',
                description:
                  'Auto Spotlight turns anonymous traffic into identifiable buyer behavior.',
              },
              {
                title: 'Clarity Replaces Guesswork',
                description:
                  'Know exactly what each shopper engaged with — before the conversation begins.',
              },
              {
                title: 'Guided Actions Drive Momentum',
                description:
                  'From pricing to trade-in to finance, buyers always know what to do next.',
              },
              {
                title: 'Intent Turns Into Quality Leads',
                description:
                  'When behavior is visible, follow-up becomes smarter — and more effective.',
              },
            ]}
            footerText=''
            imagePath={'/static/home_page/img_auto_spotlight_mobile.png'}
            openImageViewView={(_link: string) => {
              openImageViewView('/static/lot_page/img_auto_spotlight_mobile_big.png');
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            background: `url('/static/home_page/img_purple_blue_banner.png')`,
            backgroundSize: 'cover', // '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h3,
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            One Experience. Total Visibility. Better Leads.
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              // color: 'gold',
              color: 'black',
              fontWeight: 600,
              mt: 1,
            }}
          >
            From lot scan to real buyer intent — fully connected in real time.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Customer</strong> Experience' />
            </Typography>
          </GradientBorderBox>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 'bold',
            }}
          >
            Designed for How Buyers Actually Shop
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              my: 2,
            }}
          >
            A mobile-first AutoSpotlight experience that guides every
            interaction toward clarity, confidence, and next steps.
          </Typography>

          <Box
            component={'img'}
            src='/static/home_page/img_web_mobile.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            mb={{ xs: 2, sm: 4 }}
            sx={{ cursor: 'pointer' }}   // ← visual hint that it's clickable
            onClick={buildClickHandler(ZONES_MOBILE_SCREENS)}
          />

          {getBookADemoButton()}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            // background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Dealer</strong> Dashboard' />
            </Typography>
          </GradientBorderBox>

          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 'bold',
            }}
          >
            Your Command Center for Every Lead
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              my: 2,
            }}
          >
            View all customer interactions, manage sales reps, adjust
            incentives, receive real-time notifications, and track speed-to-lead
            performance — from anywhere.
          </Typography>

          {getLearnMoreAndGetStartedButtons()}

          <Box
            component={'img'}
            src='/static/home_page/img_dealer_dashboard.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            sx={{ cursor: 'pointer'}}
            onClick={buildClickHandler(ZONES_COMMAND_CENTER)}
          />
        </Box>

        <Grid
          container
          sx={{
            py: 4,
            px: '10%',
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            background: AppColors.aiGradient8,
          }}
        >
          <Grid
            size={{ xs: 12, sm: 3 }}
            textAlign={{ xs: 'center', sm: 'end' }}
          >
            <Box
              component={'img'}
              src='/static/home_page/img_dealer_dashboard_mobile.png'
              sx={{
                // maxWidth: { xs: '100%', sm: '100%' },
                height: '100%',
                cursor: 'pointer'
              }}
              onClick={buildClickHandler(ZONES_DEALERSHIP_IN_POCKET)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Typography
              sx={{
                fontSize: typographyClamps.h4,
                fontWeight: 'bold',
              }}
            >
              Your Dealership. In your pocket.
            </Typography>
            <Typography
              sx={{
                fontSize: typographyClamps.h5,
                fontWeight: 400,
                mt: { xs: 0, sm: 2 },
              }}
            >
              Stay connected to your sales floor, manager team, and respond to
              leads instantly – wherever business takes you.
            </Typography>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            // background: AppColors.aiGradient8,
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
          }}
        >
          <GradientBorderBox gradient={AppColors.aiGradient9}>
            <Typography
              sx={{ fontSize: typographyClamps.h6, color: '#1961ff' }}
            >
              <HtmlRenderer htmlString='<strong>Sales Rep</strong> Dashboard' />
            </Typography>
          </GradientBorderBox>

          <Box
            component={'img'}
            src='/static/home_page/img_sales_rep_dashboard.png'
            width={'auto'}
            maxWidth={'100%'}
            mt={{ xs: 1, sm: 2 }}
            mb={{ xs: 2, sm: 4 }}
            sx={{ cursor: 'pointer' }}
            onClick={buildClickHandler(ZONES_STACKED_SCREENS)}
          />

          {getLearnMoreAndGetStartedButtons()}

          {/* <Grid
            container
            sx={{
              py: 4,
              px: '10%',
              mt: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              background: AppColors.aiGradient8,
            }}
          >
            <Grid
              size={{ xs: 8.5, sm: 9 }}
              textAlign={{ xs: 'center', sm: 'end' }}
            >
              <Box
                component={'img'}
                src='/static/home_page/img_sales_rep_dashboard.png'
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </Grid>
            <Grid size={{ xs: 2.5, sm: 2.8 }}>
              <Box
                component={'img'}
                src='/static/home_page/img_prospect_on_lot.png'
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </Grid>
          </Grid> */}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: `url('/static/benefits_page/img_benefits_tile_bg_big.png')`,
            backgroundSize: '100% auto',
            p: { xs: 1, sm: 2 },
            position: 'relative',
          }}
        >
          <Grid container gap={{ xs: 1, sm: 2 }} justifyContent={'center'}>
            {featureCards.map((item) => {
              return (
                <Grid
                  size={{ xs: 5.7, sm: 5.8, md: 2.8 }}
                  key={item.title}
                  display={'flex'}
                >
                  <Box
                    component={'img'}
                    src={item.iconPath}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      openImageViewView(
                        (e.currentTarget as HTMLImageElement).src,
                      );
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box sx={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
          <Box
            component={'img'}
            src={'/static/home_page/img_light_blue_circle_1.png'}
            sx={{
              transform: `rotate(270deg)`,
              position: 'absolute',
              // left: 0,
              left: { xs: '-125%', sm: '-60%', md: '-50%', lg: '-20%' },
              top: 0,
            }}
          />

          <Box
            component={'img'}
            src={'/static/home_page/img_light_blue_circle_1.png'}
            sx={{
              position: 'absolute',
              transform: `rotate(90deg)`,
              // right: 0,
              right: { xs: '-125%', sm: '-70%', md: '-50%', lg: '-20%' },
              bottom: '10%',
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              right: '10%',
              top: '10%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              right: '5%',
              top: '50%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box
            component={'img'}
            src={'/static/images/ic_ai_star.png'}
            sx={{
              position: 'absolute',
              left: '10%',
              bottom: '10%',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          />

          <Box maxWidth='800px' mx='auto' pt={4} px={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                fontSize={typographyClamps.h4}
                align='center'
                // gutterBottom
                color={AppColors.vividBlueColor}
              >
                Frequently Asked Questions
              </Typography>

              {/* <Box
              component={'img'}
              src='/static/images/img_ai_robot_trimmed.png'
              sx={{
                height: { xs: '70px', ms: '120px' },
              }}
            /> */}
            </Box>

            {faqData.map((faq, parentIndex) => (
              <Box>
                <Typography
                  fontSize={typographyClamps.h6}
                  fontWeight={600}
                  // align='center'
                  gutterBottom
                  color={AppColors.textBlackColor}
                  mt={4}
                  // mb={2}
                >
                  {faq.title}
                </Typography>
                {faq.questions.map((item, index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel-${parentIndex}-${index}`}
                    onChange={handleChange(`panel-${parentIndex}-${index}`)}
                    disableGutters
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      // borderTop: index === 0 ? '1px solid #e0e0e0' : 'none',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon
                          sx={{ color: AppColors.vividBlueColor }}
                        />
                      }
                    >
                      <Typography
                        sx={{
                          color: AppColors.vividBlueColor,
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {item.question}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Typography
                        sx={{
                          color: AppColors.darkGrayColor,
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}
      
      {audioUrl && (
        <AudioModel
          onClose={audioOnClose}
          toggle={audioToggle}
          audioUrl={audioUrl ?? ''}
          title=''
        />
      )}

      <GetStartedModel
        onClose={onCloseGetStartedModel}
        toggle={toggleGetStartedModel}
        onSubmitSuccess={() => {}}
      />

      <BookDemoFormModel
        onClose={onCloseRequestDemoModel}
        toggle={toggleRequestDemoModel}
        onSubmitSuccess={() => {}}
      />

      <ImagePreviewDialog
        open={toggleImagePreviewModel}
        onClose={onCloseImagePreviewModel}
        imageSrc={previewImagePath || ''}
        imageAlt='Image'
        title='Image Preview'
      />
    </>
  );
};;;;;;

export default Home;
